import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle/component';
import { updateUserLoginMethod } from '~/data/employee';
import { useEmployeeProfile } from '~/data/employeeProfile';
import { AccountManagementMenu_OnUpdateLegacy } from '~/library/AccountManagementMenu/component';
import { useChangeLoginMethodDialog } from '~/scenes/PeopleModule/components/EmployeeActions/ChangeLoginMethodDialog/useChangeLoginMethodDialog';
import { AppDispatch } from '~/store';

type PropsType = AccountManagementMenu_OnUpdateLegacy & {
  employeeId: number;
  open: boolean;
  onClose: () => void;
};
export const ChangeLoginMethodDialog = (props: PropsType) => {
  const { open, onClose, employeeId, onUpdateLegacy } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch: AppDispatch = useDispatch();

  const { profile } = useEmployeeProfile();
  const getTitleWithEmployeeName = useCallback(
    (title) =>
      !!profile ? `${title} - ${profile.firstName} ${profile.lastName}` : title,
    [profile],
  );

  const {
    form: { control, handleSubmit },
    resetForm,
  } = useChangeLoginMethodDialog();

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleFormSubmit = useMemo(() => {
    return handleSubmit(
      (formData) => {
        dispatch(
          updateUserLoginMethod({
            employeeId,
            loginIsExternal: formData.loginIsExternal,
          }),
        )
          .then((result) => {
            if (!!onUpdateLegacy) {
              onUpdateLegacy(['changeLoginMethodUpdated']);
            }
            handleClose();
          })
          .catch((err) => {
            enqueueSnackbar(err, {
              variant: 'error',
            });
          });
      },
      (errors) => {
        Object.values(errors).forEach((fieldError) => {
          enqueueSnackbar(fieldError.message, {
            variant: 'error',
          });
        });
      },
    );
  }, [
    enqueueSnackbar,
    handleSubmit,
    handleClose,
    dispatch,
    employeeId,
    onUpdateLegacy,
  ]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <SolaForceDialogTitle onClose={handleClose}>
        {getTitleWithEmployeeName(t('laChangeLoginMethod'))}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        <Box>
          <Controller
            control={control}
            name={'loginIsExternal'}
            render={({ field: { value, onChange } }) => {
              return (
                <FormControl>
                  <FormLabel id={'login-with-chosen-method'}>
                    {t('laLoginWith')}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby={'login-with-chosen-method'}
                    defaultValue={false}
                    value={value}
                    onChange={onChange}
                    row
                  >
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label={'Solaforce'}
                    />
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label={t('laOther')}
                    />
                  </RadioGroup>
                </FormControl>
              );
            }}
          />
        </Box>

        <Typography variant={'caption'}>{t('laLoginHint')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color={'secondary'}>
          {t('cancel')}
        </Button>
        <Button onClick={handleFormSubmit}>{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
};
